import { addMonths, format, isAfter, isBefore, isSameDay, startOfDay, startOfMonth } from 'date-fns';
import { de, enUS, es } from 'date-fns/locale';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Divider, IconButton, Popover, Typography, useMediaQuery } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar, LocalizationProvider, PickersDay } from '@mui/x-date-pickers';
import { useTranslation } from '../hooks/useTranslation';

const COLORS = {
  text: '#2c3e50',
  textMuted: '#6c757d',
  primary: '#EBC122',
  border: 'rgba(235, 193, 34, 0.30)',
  borderStrong: 'rgba(235, 193, 34, 0.55)',
  bg: 'rgba(255, 255, 255, 0.96)',
  shadow: '0 20px 40px rgba(0, 0, 0, 0.10)',
};

function getLocale(language) {
  switch (language) {
    case 'en':
      return enUS;
    case 'de':
      return de;
    case 'es':
    default:
      return es;
  }
}

function toISODateString(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return format(value, 'yyyy-MM-dd');
}

function toLocalDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') return parseISODateString(value);
  return null;
}

function parseISODateString(value) {
  if (!value) return null;
  // Evitar el comportamiento de Date con strings "YYYY-MM-DD" (se interpreta como UTC),
  // que puede desplazar el día en ciertas zonas horarias y romper `isSameDay`.
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DoubleDateRangePicker = ({ startDate, endDate, onChange, minDate }) => {
  const { t, language } = useTranslation();
  const locale = useMemo(() => getLocale(language), [language]);
  const isMobile = useMediaQuery('(max-width:768px)');

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [baseMonth, setBaseMonth] = useState(() => startOfMonth(new Date()));
  const leftMonth = useMemo(() => startOfMonth(baseMonth), [baseMonth]);
  const rightMonth = useMemo(() => startOfMonth(addMonths(baseMonth, 1)), [baseMonth]);
  const monthKey = useMemo(() => format(leftMonth, 'yyyy-MM'), [leftMonth]);

  // Mantener estado local para que el resaltado (amarillo) sea inmediato dentro del modal,
  // sin esperar a que el padre re-renderice.
  const [localStartDate, setLocalStartDate] = useState(() => parseISODateString(startDate));
  const [localEndDate, setLocalEndDate] = useState(() => parseISODateString(endDate));

  useEffect(() => {
    setLocalStartDate(parseISODateString(startDate));
    setLocalEndDate(parseISODateString(endDate));
  }, [startDate, endDate]);

  const start = localStartDate;
  const end = localEndDate;
  const min = useMemo(() => (minDate ? startOfDay(new Date(minDate)) : startOfDay(new Date())), [minDate]);

  const displayText = useMemo(() => {
    if (start && end) {
      const s = format(start, 'dd/MM/yyyy', { locale });
      const e = format(end, 'dd/MM/yyyy', { locale });
      return `${s} - ${e}`;
    }
    if (start) {
      const s = format(start, 'dd/MM/yyyy', { locale });
      return `${s} - ...`;
    }
    return t('daterangepicker.select.range');
  }, [start, end, locale, t]);

  const finalizeSingleDayIfNeeded = () => {
    // Si el usuario eligió inicio pero cerró el modal sin elegir fin,
    // asumimos reserva de 1 día (fin = inicio).
    if (start && !end) {
      const startDay = startOfDay(start);
      setRange(startDay, startDay);
    }
  };

  const durationDays = useMemo(() => {
    if (!start || !end) return 0;
    return Math.ceil((startOfDay(end) - startOfDay(start)) / (1000 * 60 * 60 * 24)) + 1;
  }, [start, end]);

  const setRange = (nextStart, nextEnd) => {
    setLocalStartDate(nextStart ? startOfDay(toLocalDate(nextStart)) : null);
    setLocalEndDate(nextEnd ? startOfDay(toLocalDate(nextEnd)) : null);
    onChange?.(toISODateString(nextStart), toISODateString(nextEnd));
  };

  const handleDaySelect = (value) => {
    if (!value) return;
    const day = startOfDay(value);
    if (isBefore(day, min)) return;

    // 1) Si no hay start o ya hay rango completo: empezar rango nuevo
    if (!start || (start && end)) {
      setRange(day, '');
      return;
    }

    // 2) Si hay start pero no end: completar rango (o reordenar)
    if (start && !end) {
      const startDay = startOfDay(start);
      if (isBefore(day, startDay)) {
        setRange(day, startDay);
      } else {
        setRange(startDay, day);
      }
      // En mobile, al completar el rango cerramos automáticamente el modal.
      if (isMobile) {
        setOpen(false);
      }
    }
  };

  const handleClear = () => {
    setRange('', '');
    setOpen(false);
  };

  const RangeDay = (dayProps) => {
    const { day, outsideCurrentMonth, rangeStart, rangeEnd, rangeMin, ...other } = dayProps;
    const d = startOfDay(day);

    const isDisabled = isBefore(d, rangeMin);
    const isStart = rangeStart ? isSameDay(d, rangeStart) : false;
    const isEnd = rangeEnd ? isSameDay(d, rangeEnd) : false;
    const inRange =
      rangeStart && rangeEnd
        ? (isAfter(d, startOfDay(rangeStart)) && isBefore(d, startOfDay(rangeEnd))) || isStart || isEnd
        : isStart;

    return (
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        // Evitar que MUI aplique su "selected" (azul). Nosotros pintamos el rango manualmente.
        selected={false}
        disabled={isDisabled || other.disabled}
        sx={{
          borderRadius: 2,
          fontWeight: isStart || isEnd ? 800 : 600,
          color: isDisabled ? 'rgba(44,62,80,0.35)' : COLORS.text,
          ...(inRange && { bgcolor: 'rgba(235, 193, 34, 0.18)' }),
          ...((isStart || isEnd) && {
            bgcolor: COLORS.primary,
            color: COLORS.text,
            '&:hover': { bgcolor: '#d4a91e' },
          }),
          '&.Mui-selected': {
            bgcolor: COLORS.primary,
            color: COLORS.text,
          },
          '&.Mui-selected:hover': {
            bgcolor: '#d4a91e',
          },
        }}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <Box className="double-date-range-picker" sx={{ width: '100%' }}>
        <Box
          ref={anchorRef}
          role="button"
          onClick={() => setOpen(true)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setOpen(true);
          }}
          sx={{
            width: '100%',
            borderRadius: 2,
            border: `2px solid ${COLORS.border}`,
            bgcolor: '#fff',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            transition: 'all 200ms ease',
            '&:hover': { borderColor: COLORS.primary },
            '&:focus-visible': {
              outline: 'none',
              boxShadow: '0 0 0 3px rgba(235, 193, 34, 0.12)',
              borderColor: COLORS.primary,
            },
          }}
        >
          <CalendarIcon size={16} color={COLORS.primary} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: COLORS.text,
                fontSize: 14,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayText}
            </Typography>
            <Typography sx={{ color: COLORS.textMuted, fontSize: 12 }}>
              {t('daterangepicker.select.dates')}
            </Typography>
          </Box>
          {start && end && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              aria-label={t('daterangepicker.clear.dates')}
              sx={{ color: COLORS.textMuted }}
            >
              <X size={16} />
            </IconButton>
          )}
        </Box>

        <Popover
          open={open}
          onClose={() => {
            finalizeSingleDayIfNeeded();
            setOpen(false);
          }}
          anchorEl={anchorRef.current}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1.5,
                borderRadius: 3,
                border: `1px solid ${COLORS.border}`,
                bgcolor: COLORS.bg,
                boxShadow: COLORS.shadow,
                overflow: 'hidden',
              },
            },
          }}
        >
          <Box sx={{ p: 2, minWidth: isMobile ? 320 : 720 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography sx={{ color: COLORS.text, fontSize: 14, fontWeight: 800 }}>
                  {t('daterangepicker.select.range')}
                </Typography>
                <Typography sx={{ color: COLORS.textMuted, fontSize: 12 }}>
                  {start && !end ? t('daterangepicker.end.date') : t('daterangepicker.start.date')}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  finalizeSingleDayIfNeeded();
                  setOpen(false);
                }}
                aria-label={t('daterangepicker.close')}
                sx={{ color: COLORS.textMuted }}
              >
                <X size={18} />
              </IconButton>
            </Box>

            <Divider sx={{ borderColor: 'rgba(235, 193, 34, 0.20)', mb: 2 }} />

            <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: COLORS.text, fontSize: 12, fontWeight: 700, mb: 1 }}>
                  {t('daterangepicker.start.date')}
                </Typography>
                <DateCalendar
                  key={`left-${monthKey}`}
                  // Importante: no atar el mes mostrado al "value".
                  // Si se selecciona un rango dentro del mes actual, MUI puede mover ambos calendarios al mismo mes.
                  // La selección la pintamos manualmente en `RangeDay`.
                  value={null}
                  onChange={handleDaySelect}
                  minDate={min}
                  // En algunas versiones, `referenceDate` no gana a `minDate`.
                  // `defaultCalendarMonth` fuerza el mes inicial mostrado.
                  defaultCalendarMonth={leftMonth}
                  referenceDate={leftMonth}
                  onMonthChange={(newMonth) => setBaseMonth(startOfMonth(newMonth))}
                  slots={{ day: RangeDay }}
                  slotProps={{
                    day: {
                      rangeStart: start ? startOfDay(start) : null,
                      rangeEnd: end ? startOfDay(end) : null,
                      rangeMin: min,
                    },
                  }}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${COLORS.border}`,
                    bgcolor: '#fff',
                    '& .MuiPickersCalendarHeader-label': { fontWeight: 800, color: COLORS.text },
                    '& .MuiPickersArrowSwitcher-button': { color: COLORS.textMuted },
                  }}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: COLORS.text, fontSize: 12, fontWeight: 700, mb: 1 }}>
                  {t('daterangepicker.end.date')}
                </Typography>
                <DateCalendar
                  key={`right-${monthKey}`}
                  value={null}
                  onChange={handleDaySelect}
                  // No bloquear días anteriores al inicio actual: permite al usuario empezar un nuevo rango
                  // o seleccionar una fecha anterior sin tener que limpiar primero.
                  minDate={min}
                  defaultCalendarMonth={rightMonth}
                  referenceDate={rightMonth}
                  onMonthChange={(newMonth) => setBaseMonth(startOfMonth(addMonths(newMonth, -1)))}
                  slots={{ day: RangeDay }}
                  slotProps={{
                    day: {
                      rangeStart: start ? startOfDay(start) : null,
                      rangeEnd: end ? startOfDay(end) : null,
                      rangeMin: min,
                    },
                  }}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${COLORS.border}`,
                    bgcolor: '#fff',
                    '& .MuiPickersCalendarHeader-label': { fontWeight: 800, color: COLORS.text },
                    '& .MuiPickersArrowSwitcher-button': { color: COLORS.textMuted },
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                mt: 2,
                pt: 1.5,
                borderTop: '1px solid rgba(235, 193, 34, 0.20)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: COLORS.textMuted, fontSize: 12 }}>
                  {t('daterangepicker.duration')}
                </Typography>
                <Typography sx={{ color: COLORS.text, fontSize: 13, fontWeight: 800 }}>
                  {durationDays ? `${durationDays} ${durationDays === 1 ? t('date.day') : t('date.days')}` : '—'}
                </Typography>
              </Box>
              {start && end && (
                <Typography sx={{ color: COLORS.textMuted, fontSize: 12, textAlign: 'right' }}>
                  9:00 → 21:00
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Box
                component="button"
                type="button"
                onClick={() => {
                  finalizeSingleDayIfNeeded();
                  setOpen(false);
                }}
                sx={{
                  border: 'none',
                  borderRadius: 2,
                  bgcolor: COLORS.primary,
                  color: COLORS.text,
                  fontSize: 14,
                  fontWeight: 700,
                  px: 3,
                  py: 1.2,
                  cursor: 'pointer',
                  transition: 'background-color 150ms ease',
                  '&:hover': { bgcolor: '#d4a91e' },
                }}
              >
                {t('daterangepicker.confirm')}
              </Box>
            </Box>
          </Box>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
};

export default DoubleDateRangePicker;

